<script setup>
const props = defineProps({
  data: Object
})

function calculateEndValue(stat) {
  return 100 - stat
}

onMounted(function(){

  function animateIfVisible() {
    let charts = document.getElementsByClassName('donut-chart')
    for (let i = 0; i < charts.length; i++) {
      let el = charts[i]
      if (isInViewport(el) && !el.classList.contains('animate-statistic')) {
        activateAnimation(el)
      }
    }
  }

  function isInViewport(element) {
    const rect = element.getBoundingClientRect()
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }

  function activateAnimation(el) {
    el.classList.add('animate-statistic')
    donutFillAnimation(el);
    numberIncrementAnimation(el);
  }

  function donutFillAnimation(el) {
    const strokeDashArray = el.getAttribute('data-stroke-dasharray')
    const svgCircle = el.querySelector('.donut-fill')
    svgCircle.setAttribute('stroke-dasharray', strokeDashArray)
  }

  function numberIncrementAnimation(el) {
    const $stat = el.querySelector('.donut-stat')
    const { prefix = '', number = 0, suffix = '' } = parseStatistic($stat)
    animateValue($stat, 0, number, 1500, prefix, suffix)
  }

  function parseStatistic(el) {
    const matches = el.innerHTML.match(/^([\D])?([\d\.]+)(.+)?$/)
    return matches ? { prefix: matches[1], number: matches[2], suffix: matches[3] } : {}
  }

  function animateValue(el, start, end, duration, prefix, suffix) {
    let startTimestamp = null
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)
      el.innerHTML = prefix + Math.floor(progress * (end - start) + start) + suffix
      if (progress < 1) {
        window.requestAnimationFrame(step)
      }
    }
    window.requestAnimationFrame(step)
  }  

  /**
   * Check if elements are in the viewport before registering scroll
   * listener. Handles case where elements are initialized in the
   * viewport before user scroll.
   */
  animateIfVisible()

  
  window.addEventListener('scroll', function () {
    animateIfVisible()
  })
})

</script>

<template>
  <div class="block animated-statistics">
    <div class="container">
      <div class="row justify-content-around align-items-center">
        <div v-for="stat in (Array.isArray(data?.statistics) ? data.statistics : [])" class="col-12 col-md px-0 my-4 my-md-8">
          <div class="donut-chart text-center mx-xl-3"
            :data-stroke-dasharray="(stat?.FillPercentage ?? 0) + ',' + calculateEndValue(stat?.FillPercentage ?? 0)"
            >
            <div class="donut position-relative mx-auto">
              <svg width="100%" height="100%" viewBox="0 0 40 40">
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="15%" stop-color="#57c86f"></stop>
                    <stop offset="55%" stop-color="#4a73eb"></stop>
                    <stop offset="100%" stop-color="#7f31c0"></stop>
                  </linearGradient>
                </defs>
                <circle cx="20" cy="20" r="15.91549430918954" fill="transparent"></circle>
                <circle cx="20" cy="20" r="15.91549430918954" fill="transparent" stroke="#eee" stroke-width="2">
                </circle>
                <circle class="donut-fill" cx="20" cy="20" r="15.91549430918954" fill="transparent"
                  stroke="url(#gradient)" stroke-width="2"
                  stroke-dasharray="0,100"
                  stroke-dashoffset="25" 
                  stroke-linecap="round"></circle>
              </svg>
              <span :class="'donut-stat d-block h1 mx-auto mb-0 w-75 length-' + (stat?.Statistic || '').length">
                {{ stat?.Statistic }}
              </span>
            </div>
            <p class="donut-caption h4 text-center mx-auto">{{ stat?.Caption }}</p>
          </div>
        </div>
      </div>
      <div v-if="data?.referenceText" class="row">
        <div class="col-12">
          <p class="reference-text text-center" v-html="data?.referenceText"></p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.animated-statistics {

  .donut-chart {
    font-size: 5rem;

    @include media-breakpoint-between(md, lg) {
      font-size: 3.75rem;
    }

    .donut {
      max-width: 4.25em;
      height: 4.25em;

      &>svg {
        position: absolute;
        top: 0;
        left: 0;
      }

      .donut-fill {
        transition: stroke-dasharray 1.5s cubic-bezier(.4,0,0,1);
      }

      .donut-stat {
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        font-size: 1em;
        transform: translateY(-50%);
        
        &.length-4 {
          font-size: .95em;
        }

        &.length-5 {
          font-size: .85em;
        }

        &.length-6 {
          font-size: .75em;
        }
      }
    }

    .donut-caption {
      max-width: 15em;
      font-size: .225em;
    }
  }

  &.animation-active {
      
    .donut-chart {
      opacity: 0;
      transition: all 1s cubic-bezier(.4,0,0,1);

      &.animate-statistic {
        opacity: 1;
      }
    }
  }
}
</style>